/**
 * Platform settings — editable from the admin panel without redeploy.
 *
 * Stored in localStorage under `neyra.platform.settings`. When Supabase is
 * configured, changes are mirrored to the `platform_settings` table so the
 * admin can tweak live values on a real backend. Also persisted via the
 * FastAPI backend (`/api/platform/settings`) so settings survive across
 * devices even without Supabase.
 */
import axios from "axios";
import { mirror } from "@/lib/syncStore";
import { SUPABASE_TABLES } from "@/lib/supabaseClient";

const KEY = "neyra.platform.settings";
const BUS_EVT = "neyra-platform-settings";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const emit = () => window.dispatchEvent(new Event(BUS_EVT));

export const subscribeSettings = (cb) => {
    window.addEventListener(BUS_EVT, cb);
    return () => window.removeEventListener(BUS_EVT, cb);
};

export const DEFAULT_SETTINGS = {
    premiumPriceMonthlyNgn:    5000,
    premiumPriceYearlyNgn:    50000,
    verificationFeePersonalNgn: 3000,
    verificationFeeBusinessNgn: 5000,
    verificationFeeNgn:        3000, // legacy alias = personal
    boostPrice24hNgn:           500,
    boostPrice3dNgn:           1500,
    boostPrice7dNgn:           3000,
    commissionPercent:           10,
    referralRewardNgn:         1000,
    maintenanceMode:          false,
};

export const getPlatformSettings = () => {
    try {
        const raw = localStorage.getItem(KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return { ...DEFAULT_SETTINGS, ...parsed };
    } catch {
        return { ...DEFAULT_SETTINGS };
    }
};

/** Pull current settings from backend (FastAPI) and refresh local cache. */
export const refreshPlatformSettingsFromBackend = async () => {
    if (!BACKEND_URL) return getPlatformSettings();
    try {
        const { data } = await axios.get(`${BACKEND_URL}/api/platform/settings`);
        const remote = data?.settings || {};
        const next = { ...DEFAULT_SETTINGS, ...remote };
        localStorage.setItem(KEY, JSON.stringify(next));
        emit();
        return next;
    } catch {
        return getPlatformSettings();
    }
};

export const updatePlatformSettings = async (patch) => {
    const next = { ...getPlatformSettings(), ...patch };
    localStorage.setItem(KEY, JSON.stringify(next));
    emit();
    // Persist via backend (FastAPI). Best-effort — won't block on failure.
    if (BACKEND_URL) {
        try { await axios.put(`${BACKEND_URL}/api/platform/settings`, next); } catch {}
    }
    // Mirror to Supabase when configured.
    try {
        await mirror(SUPABASE_TABLES.SETTINGS, { id: "singleton", ...next, updated_at: new Date().toISOString() });
    } catch {}
    return next;
};
